import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { durationColor } from '@/lib/badgeColors';
import { CheckCircle, ArrowRight, Clock, Headphones, Package, Sparkles, ShieldCheck, X, Loader2 } from 'lucide-react';

export default function UpsellPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    if (!sessionId) { navigate('/'); return; }
    api.get(`/upsell/for-session/${sessionId}`).then(r => {
      setOffers(r.data.offers || []);
      setOriginalProduct(r.data.product);
      if (!r.data.offers?.length) {
        navigate(`/checkout/success?session_id=${sessionId}&no_upsell=true`);
      }
    }).catch(() => {
      navigate(`/checkout/success?session_id=${sessionId}&no_upsell=true`);
    }).finally(() => setLoading(false));
  }, [sessionId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="upsell-loading">
        <Loader2 className="w-8 h-8 text-[#1A9DA5] animate-spin" />
      </div>
    );
  }

  const currentOffer = offers[currentOfferIndex];
  if (!currentOffer) {
    navigate(`/checkout/success?session_id=${sessionId}&no_upsell=true`);
    return null;
  }

  const offeredProduct = currentOffer.offered_product || {};
  const bundledProducts = currentOffer.bundled_products || [];
  const allProducts = [offeredProduct, ...bundledProducts].filter(Boolean);
  const upsellPrice = currentOffer.price || offeredProduct.package_price || offeredProduct.price || 0;
  const individualTotal = allProducts.reduce((sum, p) => sum + parseFloat(p?.price || 0), 0);
  const savings = individualTotal > upsellPrice ? (individualTotal - upsellPrice).toFixed(2) : null;
  const isBundle = bundledProducts.length > 0;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post('/upsell/accept', {
        offer_id: currentOffer.id,
        session_id: sessionId,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch {
      moveToNext();
    } finally { setAccepting(false); }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await api.post('/upsell/decline', {
        offer_id: currentOffer.id,
        session_id: sessionId,
      });
    } catch {}
    setDeclining(false);
    moveToNext();
  };

  const moveToNext = () => {
    if (currentOfferIndex + 1 < offers.length) {
      setCurrentOfferIndex(prev => prev + 1);
    } else {
      navigate(`/checkout/success?session_id=${sessionId}&no_upsell=true`);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="upsell-page">
      <header className="border-b bg-card">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" />
            <span className="font-bold font-['Outfit']">Wayfinder</span>
          </Link>
          <Separator orientation="vertical" className="h-6" />
          <Badge className="bg-green-500/10 text-green-600 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />Payment Complete
          </Badge>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A9DA5]/10 text-[#1A9DA5] text-sm font-medium mb-4" data-testid="upsell-badge">
            <Sparkles className="w-4 h-4" />
            {isBundle ? 'Exclusive Bundle Offer' : 'Special One-Time Offer'}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-2">
            Wait — before you go!
          </h1>
          <p className="text-muted-foreground">
            You just purchased <strong>{originalProduct?.title}</strong>. Here's an exclusive deal:
          </p>
        </div>

        <Card className="border-2 border-[#1A9DA5]/30 overflow-hidden" data-testid="upsell-offer-card">
          <div className="sparky-gradient px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                {isBundle ? <Package className="w-5 h-5" /> : <Headphones className="w-5 h-5" />}
              </div>
              <div>
                <h2 className="font-bold text-lg font-['Outfit']">{currentOffer.headline || offeredProduct.title}</h2>
                <p className="text-white/80 text-xs">
                  {isBundle ? `${allProducts.length} guides included` : 'Add to Your Library'}
                </p>
              </div>
            </div>
            {savings && (
              <Badge className="bg-[#F0B429] text-white border-[#F0B429] text-sm">
                Save ${savings}
              </Badge>
            )}
          </div>

          <CardContent className="p-6 space-y-5">
            {offeredProduct.short_description && !isBundle && (
              <p className="text-sm leading-relaxed">{offeredProduct.short_description}</p>
            )}

            {/* BUNDLED PRODUCTS */}
            {isBundle && (
              <div className="space-y-3" data-testid="bundled-products-list">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">What's Included</p>
                {allProducts.map((p, i) => (
                  <div key={p?.id || i} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20" data-testid={`bundled-item-${i}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: i === 0 ? '#1A9DA510' : i % 2 === 0 ? '#E8735A10' : '#F0B42910' }}>
                      {p?.type === 'package'
                        ? <Package className="w-4 h-4" style={{ color: i === 0 ? '#1A9DA5' : i % 2 === 0 ? '#E8735A' : '#F0B429' }} />
                        : <Headphones className="w-4 h-4" style={{ color: i === 0 ? '#1A9DA5' : i % 2 === 0 ? '#E8735A' : '#F0B429' }} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{p?.title}</p>
                      {p?.short_description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{p?.short_description}</p>}
                      <div className="flex items-center gap-3 mt-1.5">
                        {p?.duration_minutes && (
                          <span className={`text-xs flex items-center gap-1 px-1.5 py-0.5 rounded ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}><Clock className="w-3 h-3" />{p.duration_minutes} min</span>
                        )}
                        {p?.category && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.category}</Badge>}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground line-through">${p?.price}</span>
                  </div>
                ))}
              </div>
            )}

            {/* BULLETS */}
            {!isBundle && (currentOffer.bullets?.length > 0 || offeredProduct.outcomes?.length > 0) && (
              <div className="space-y-2">
                {(currentOffer.bullets?.length ? currentOffer.bullets : offeredProduct.outcomes || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#1A9DA5] shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            )}

            {!isBundle && offeredProduct.duration_minutes && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${durationColor(offeredProduct.duration_minutes).bg} ${durationColor(offeredProduct.duration_minutes).text}`}><Clock className="w-3 h-3" />{offeredProduct.duration_minutes} min</span>
                {offeredProduct.category && <Badge variant="outline" className="text-xs">{offeredProduct.category}</Badge>}
              </div>
            )}

            <Separator />

            {/* PRICE */}
            <div className="text-center" data-testid="upsell-price-section">
              {savings && (
                <p className="text-muted-foreground line-through text-sm mb-1">
                  ${individualTotal.toFixed(2)} if purchased separately
                </p>
              )}
              <p className="text-4xl font-bold font-['Outfit'] text-[#1A9DA5]">${upsellPrice}</p>
              <p className="text-xs text-muted-foreground mt-1">one-time payment · instant access to all</p>
            </div>

            <Button
              data-testid="upsell-accept-btn"
              onClick={handleAccept}
              disabled={accepting}
              className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              {accepting
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                : <><ArrowRight className="w-5 h-5 mr-2" />Yes, Add This to My Order</>
              }
            </Button>

            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-green-500" />Secure checkout</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Instant access</span>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <button
            data-testid="upsell-decline-btn"
            onClick={handleDecline}
            disabled={declining}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors inline-flex items-center gap-1"
          >
            {declining ? 'Redirecting...' : 'No thanks, take me to my purchase'}
            {!declining && <X className="w-3 h-3" />}
          </button>
        </div>

        {offers.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-6">
            {offers.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === currentOfferIndex ? 'bg-[#1A9DA5]' : i < currentOfferIndex ? 'bg-[#1A9DA5]/40' : 'bg-muted'}`} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
